import { type ConditionalRoutingRule, type IdentifierRecord } from '@myplayer/schema';
import { IconChevronLeft, IconChevronRight } from '@myplayer/ui';
import { type ReactElement } from 'react';
import { type AdminUserSession } from './auth-context.js';
import { Button, Tag } from './components/ui/primitives.js';
import {
  callCancelScheduledChangeFunction,
  callSetScheduledChangeFunction,
  type CancelScheduledChangeCallablePayload,
  type CancelScheduledChangeCallableResult,
  type SetScheduledChangeCallablePayload,
  type SetScheduledChangeCallableResult,
} from './firebase.js';
import { buildScheduledRulesList, resolveScheduledChange } from './products-helpers.js';
import { QrCustomiserCard } from './qr-customiser.js';
import {
  type GeneratedGs1QrPrintAssets,
  type Gs1SymbolSpecTableId,
  type QrExportFormat,
} from './qr-generator.js';

export interface ScheduleActionParams {
  readonly session: AdminUserSession | null;
  readonly selectedRecord: IdentifierRecord | null;
  readonly editScheduledDate?: string | undefined;
  readonly editScheduledUrl?: string | undefined;
  readonly onSetScheduledChange?:
    | ((p: SetScheduledChangeCallablePayload) => Promise<SetScheduledChangeCallableResult>)
    | undefined;
  readonly onCancelScheduledChange?:
    | ((p: CancelScheduledChangeCallablePayload) => Promise<CancelScheduledChangeCallableResult>)
    | undefined;
  readonly setLinkFormError: (err: string | null) => void;
  readonly setLinkFormSuccess: (msg: string | null) => void;
  readonly setSavingDraft: (v: boolean) => void;
  readonly onScheduledRulesUpdated: (
    identifierKey: string,
    nextRules: readonly ConditionalRoutingRule[],
    version: number,
  ) => void;
}

export async function saveProductScheduledChangeAction(p: ScheduleActionParams): Promise<boolean> {
  if (!p.session || !p.selectedRecord) {
    p.setLinkFormError('No product selected.');
    return false;
  }
  const rec = p.selectedRecord;
  const res = buildScheduledRulesList({
    existingRules: rec.draft?.rules ?? rec.rules ?? [],
    scheduledDateIso: p.editScheduledDate ?? null,
    scheduledTargetUrl: p.editScheduledUrl ?? null,
  });
  if (!res.ok) {
    p.setLinkFormError(res.error);
    return false;
  }
  const rule = res.rules.find((r) => r.id.startsWith('schedule-'));
  if (!rule?.condition?.validFrom) {
    p.setLinkFormError('Please choose a date and time for the scheduled link change.');
    return false;
  }
  p.setSavingDraft(true);
  try {
    const response = await (p.onSetScheduledChange ?? callSetScheduledChangeFunction)({
      orgId: p.session.orgId,
      workspaceId: rec.workspaceId || p.session.activeWorkspaceId,
      identifierKey: rec.identifierKey,
      targetUrl: rule.targetHref,
      targetHref: rule.targetHref,
      validFrom: rule.condition.validFrom,
      ruleId: rule.id,
    });
    p.onScheduledRulesUpdated(
      rec.identifierKey,
      res.rules,
      Math.max(response.version, (rec.version ?? 1) + 1),
    );
    const updated = resolveScheduledChange({
      ...rec,
      rules: res.rules,
      ...(rec.draft ? { draft: { ...rec.draft, rules: res.rules } } : {}),
    });
    p.setLinkFormSuccess(
      updated
        ? `Saved — Scheduled for ${updated.formattedDate} · Changes to ${updated.targetHref} on ${updated.formattedDate}`
        : 'Saved — Live now',
    );
    return true;
  } catch (err: unknown) {
    p.setLinkFormError(
      err instanceof Error ? err.message : 'Could not save the scheduled change. Please try again.',
    );
    return false;
  } finally {
    p.setSavingDraft(false);
  }
}

export async function cancelProductScheduledChangeAction(
  p: ScheduleActionParams,
): Promise<boolean> {
  if (!p.session || !p.selectedRecord) {
    p.setLinkFormError('No product selected.');
    return false;
  }
  const rec = p.selectedRecord;
  const res = buildScheduledRulesList({
    existingRules: rec.draft?.rules ?? rec.rules ?? [],
    scheduledDateIso: null,
    scheduledTargetUrl: null,
  });
  if (!res.ok) {
    p.setLinkFormError(res.error);
    return false;
  }
  p.setSavingDraft(true);
  try {
    const response = await (p.onCancelScheduledChange ?? callCancelScheduledChangeFunction)({
      orgId: p.session.orgId,
      workspaceId: rec.workspaceId || p.session.activeWorkspaceId,
      identifierKey: rec.identifierKey,
      ruleId: 'schedule-1',
    });
    p.onScheduledRulesUpdated(
      rec.identifierKey,
      res.rules,
      Math.max(response.version, (rec.version ?? 1) + 1),
    );
    p.setLinkFormSuccess('Saved — Live now');
    return true;
  } catch (err: unknown) {
    p.setLinkFormError(
      err instanceof Error ? err.message : 'Could not cancel the scheduled change.',
    );
    return false;
  } finally {
    p.setSavingDraft(false);
  }
}

export function ProductDrawerNavActions({
  selectedRecord,
  filteredRecords,
  disabled = false,
  onSelectRecord,
}: {
  readonly selectedRecord: IdentifierRecord;
  readonly filteredRecords: readonly IdentifierRecord[];
  readonly disabled?: boolean;
  readonly onSelectRecord: (identifierKey: string) => void;
}): ReactElement | null {
  const currentIndex = filteredRecords.findIndex(
    (record) => record.identifierKey === selectedRecord.identifierKey,
  );
  if (currentIndex === -1 || filteredRecords.length <= 1) return null;
  const prevRecord = currentIndex > 0 ? filteredRecords[currentIndex - 1] : undefined;
  const nextRecord =
    currentIndex < filteredRecords.length - 1 ? filteredRecords[currentIndex + 1] : undefined;

  return (
    <div data-testid="product-drawer-nav" className="flex items-center gap-1.5">
      <Tag variant="outline" data-testid="product-drawer-nav-position" className="tabular-nums">
        {currentIndex + 1} / {filteredRecords.length}
      </Tag>
      <Button
        type="button"
        variant="secondary"
        size="icon"
        data-testid="prev-product-drawer-btn"
        aria-label="Previous product"
        title={
          prevRecord
            ? `Previous: ${prevRecord.draft?.title ?? prevRecord.title ?? prevRecord.primaryKey}`
            : 'Previous product'
        }
        disabled={disabled || prevRecord === undefined}
        onClick={() => prevRecord && onSelectRecord(prevRecord.identifierKey)}
      >
        <IconChevronLeft />
      </Button>
      <Button
        type="button"
        variant="secondary"
        size="icon"
        data-testid="next-product-drawer-btn"
        aria-label="Next product"
        title={
          nextRecord
            ? `Next: ${nextRecord.draft?.title ?? nextRecord.title ?? nextRecord.primaryKey}`
            : 'Next product'
        }
        disabled={disabled || nextRecord === undefined}
        onClick={() => nextRecord && onSelectRecord(nextRecord.identifierKey)}
      >
        <IconChevronRight />
      </Button>
    </div>
  );
}

export interface ProductQrCardProps {
  readonly selectedRecord: IdentifierRecord;
  readonly effectiveResolverHost: string;
  readonly qrTableId: Gs1SymbolSpecTableId;
  readonly setQrTableId?: (tableId: Gs1SymbolSpecTableId) => void;
  readonly currentQrPreview: GeneratedGs1QrPrintAssets | null;
  readonly lastDownloadedQr: {
    readonly format: QrExportFormat;
    readonly assets: GeneratedGs1QrPrintAssets;
  } | null;
  readonly onDownloadQr: (format: QrExportFormat, xDimensionMm?: number) => void;
  readonly onOpenQrCheck?: () => void;
}

export function ProductQrCard({
  selectedRecord,
  effectiveResolverHost,
  qrTableId,
  currentQrPreview,
  lastDownloadedQr,
  onDownloadQr,
  onOpenQrCheck,
}: ProductQrCardProps): ReactElement {
  const recordBrand = selectedRecord.draft?.brand ?? selectedRecord.brand;
  return (
    <QrCustomiserCard
      cardTestId="qr-download-card"
      title="QR code"
      subtitle="Ready to print on packaging or share"
      host={effectiveResolverHost}
      ai={selectedRecord.ai}
      primaryKey={selectedRecord.primaryKey}
      fileSlug={selectedRecord.identifierKey}
      tableId={qrTableId}
      workspaceId={selectedRecord.workspaceId}
      {...(recordBrand !== undefined && recordBrand.trim().length > 0
        ? { brandName: recordBrand }
        : {})}
      precomputedGs1Assets={currentQrPreview}
      externalDownloadStatus={lastDownloadedQr}
      onDownloadGs1={onDownloadQr}
      {...(onOpenQrCheck !== undefined ? { onOpenQrCheck } : {})}
    />
  );
}
